<script>
import Icon from '@/components/Icon'
import Action from '@/components/Action'

export default {
  name: 'SideBar',

  components: {
    Icon,
    Action,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    isInFocus: {
      type: Boolean,
      default: false,
    },
  },

  mounted () {
    if (this.active) {
      this.lockBodyScroll()
    }
  },

  beforeDestroy () {
    this.unlockBodyScroll()
  },

  watch: {
    active (newValue) {
      if (newValue) {
        this.lockBodyScroll()
        // Verifica o conteúdo do slot após a sidebar abrir
        this.$nextTick(() => {
          this.checkBodyContent()
        })
      } else {
        this.unlockBodyScroll()
      }
    },
  },

  methods: {
    closeSideBar () {
      this.isClosing = true
      // Aguarda a animação terminar antes de emitir o evento
      setTimeout(() => {
        this.$emit('close')
        // Reset da flag após um pequeno delay para evitar flicker
        setTimeout(() => {
          this.isClosing = false
        }, 50)
      }, 400)
    },

    closeWithAnimation () {
      if (this.active && !this.isClosing) {
        this.closeSideBar()
      }
    },

    handleOverlayClick () {
      this.closeSideBar()
    },

    lockBodyScroll () {
      // Salva a posição atual do scroll
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop

      document.body.style.top = `-${this.scrollPosition}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
    },

    unlockBodyScroll () {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflow = ''

      // Restaura a posição do scroll
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition)
        this.scrollPosition = undefined
      }
    },

    checkBodyContent () {
      if (this.active && !this.hasBodyContent) {
        this.$emit('close')
      }
    },

  },

  data () {
    return {
      scrollPosition: undefined,
      isClosing: false,
    }
  },

  computed: {
    sidebarClasses () {
      return {
        'sidebar-content': true,
        'sidebar-closing': this.isClosing,
      }
    },

    hasBodyContent () {
      const bodySlot = this.$slots.body
      if (!bodySlot || bodySlot.length === 0) return false

      // Verifica se há VNodes com conteúdo real
      return bodySlot.some(vnode => {
        if (vnode.componentOptions) return true // Componente
        if (vnode.children && vnode.children.length > 0) return true // Elemento com filhos
        if (vnode.text && vnode.text.trim() !== '') return true // Texto não vazio
        return false
      })
    },
  },
}
</script>

<template>
  <div v-if="active" :class="$classes['sidebar-overlay']" @click="handleOverlayClick">
    <div :class="[$classes['sidebar-content'], { [$classes['sidebar-closing']]: isClosing }]" @click.stop>
      <div :class="$classes['sidebar-header']">
        <h3 :class="$classes['sidebar-title']"></h3>
        <action color="secondary" variant="flat" type="button" @click="closeSideBar">
          <icon name="times" />
        </action>
      </div>
      <div :class="$classes['sidebar-body']">
        <slot name="body"></slot>
      </div>
    </div>
  </div>
</template>

<style module>
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(54, 59, 91, 0.75);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
}

.sidebar-content {
  width: 70%;
  height: 100%;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  box-shadow:
    -8px 0 32px rgba(0, 0, 0, 0.12),
    -4px 0 16px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.4s cubic-bezier(0.00, 1.06, 0.64, 1) forwards;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.sidebar-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #292929;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.sidebar-body {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  background: #F3F4F3;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.3) transparent;
}

.sidebar-body::-webkit-scrollbar {
  width: 6px;
}

.sidebar-body::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-body::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 3px;
}

.sidebar-body::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(100%);
  }
}

.sidebar-closing {
  animation: slideOut 0.4s ease-out forwards;
}

/* Responsive design */
@media (max-width: 768px) {
  .sidebar-content {
    width: 100%;
    border-radius: 0;
    animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .sidebar-header {
    padding: 20px 24px;
  }

  .sidebar-title {
    font-size: 18px;
  }

  .sidebar-body {
    padding: 24px;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%) scale(0.95);
      opacity: 0;
    }

    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(0) scale(1);
      opacity: 1;
    }

    to {
      transform: translateY(100%) scale(0.95);
      opacity: 0;
    }
  }

  .sidebar-closing {
    animation: slideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
}

@media (max-width: 480px) {
  .sidebar-header {
    padding: 15px 20px;
  }

  .sidebar-body {
    padding: 20px;
  }
}
</style>
